/* eslint-disable import/order -- path alias + mixed external groups */
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

import { saveApplicationCpa } from 'stores/cpa/cpa-utils';

import {
  AuthorizedCpaUser,
  postApiV5AuthMagicLinksHandoffWithBearer,
} from '@liscio/api';
import { useAuthStore, setAuthSession, useCpaStore } from '@liscio/common';

import { shouldRedirectToFirmSubdomain } from '../utils/clientHandoffFromMHost';

/**
 * Temporary hook for backwards compatibility
 */
export const useSetLoggedInUserSession = () => {
  const setAuthToken = useAuthStore(({ setAuthToken }) => setAuthToken);
  const setAppCpa = useCpaStore(({ setAppCpa }) => setAppCpa);

  const setLoggedInUserSession = useCallback(
    async (loggedUser: AuthorizedCpaUser) => {
      const target = shouldRedirectToFirmSubdomain(loggedUser);
      const token = loggedUser.auth_token;
      if (target && token) {
        try {
          // Same contract as orval `postApiV5AuthMagicLinksHandoff`, with Bearer: the store has no token yet.
          const data = await postApiV5AuthMagicLinksHandoffWithBearer(
            { cpa_site_url: target.cpaSiteUrl },
            token
          );
          const redirectUrl = data.redirect_url?.trim();
          if (!redirectUrl) {
            Sentry.captureMessage(
              'magic_links handoff returned empty redirect_url',
              'warning'
            );
          } else {
            window.location.replace(redirectUrl);
            return;
          }
        } catch (err) {
          Sentry.captureException(err);
          // Mint failed — fall through; RequireFirmEmployeeRoute handles visible state.
        }
      }

      const cpaToSave = {
        logo: loggedUser.cpa_data?.cpa_logo_url || '',
        name: loggedUser.cpa_data?.cpa_name || '',
        cpa_uuid: loggedUser?.cpa_data?.cpa_uuid,
        custom_liscio_url: loggedUser?.cpa_data?.company_url,
        is_current_cpa: true,
      };
      setAuthToken(loggedUser.auth_token!);
      setAuthSession({
        auth_token: loggedUser.auth_token,
        email: loggedUser.email,
      });
      setAppCpa(cpaToSave);
      await saveApplicationCpa(cpaToSave);
    },
    [setAppCpa, setAuthToken]
  );

  return setLoggedInUserSession;
};
